import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { AccessDenied, errorStatus } from '@/components/RouteError';
import { usePageHeader } from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { AccountEditDialog } from '@/components/finance/AccountEditDialog';
import { AccountsTable } from '@/components/finance/AccountsTable';
import { BalancesGrid } from '@/components/finance/BalancesGrid';
import { errorMessage } from '@/lib/errors';
import { financeAccountsQuery, financeBalancesQuery } from '@/lib/finance';
import { usePermissions } from '@/lib/me';

export const Route = createFileRoute('/_app/finance/accounts/')({
  component: AccountsPage,
});

export function AccountsPage() {
  const { can } = usePermissions();
  // Empty = "today in the business timezone", decided by the server. Every
  // balance row carries the as_of it was computed for, so the grid says which
  // date it is showing instead of this page guessing one.
  const [asOf, setAsOf] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [creating, setCreating] = useState(false);
  const accounts = useQuery(financeAccountsQuery(showInactive));
  const balances = useQuery(financeBalancesQuery(asOf || undefined));

  usePageHeader({
    subtitle: accounts.data ? `${accounts.data.length} accounts` : undefined,
    actions: can('finance.manage') ? (
      <Button variant="primary" size="sm" onClick={() => setCreating(true)}>
        <Plus className="h-4 w-4" />
        New account
      </Button>
    ) : undefined,
  });

  if (errorStatus(accounts.error) === 403) return <AccessDenied />;

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-end gap-4">
        <Input
          label="Balances as of"
          type="date"
          value={asOf}
          onChange={(e) => setAsOf(e.target.value)}
          hint={asOf ? 'Clear the field for today' : 'Empty = today'}
        />
        <Checkbox
          label="Show inactive accounts"
          checked={showInactive}
          onChange={(e) => setShowInactive(e.target.checked)}
        />
      </div>

      {balances.isError ? (
        <p role="alert" className="text-sm text-destructive">
          {errorMessage(balances.error)}
        </p>
      ) : balances.isLoading ? (
        <p className="text-sm text-muted">Loading balances…</p>
      ) : (
        <BalancesGrid accounts={accounts.data ?? []} balances={balances.data ?? []} />
      )}

      {accounts.isError ? (
        <p role="alert" className="text-sm text-destructive">
          {errorMessage(accounts.error)}
        </p>
      ) : accounts.isLoading ? (
        <p className="text-sm text-muted">Loading accounts…</p>
      ) : (
        <AccountsTable accounts={accounts.data ?? []} balances={balances.data} />
      )}

      {creating && <AccountEditDialog onClose={() => setCreating(false)} />}
    </div>
  );
}
